import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import {
  CContainer,
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import { CChartDoughnut } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import periodPrefsStore from "../../store/PeriodPrefsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import ErrorAlert from "../UI/Status/ErrorAlert";
import { SkeletonChart } from "../UI/Skeleton/Skeleton";
import { getOrderTypes, getOrderOrigins } from "../../api/reportsApi";
import { buildPieItems, fmtInt } from "../../utils/chartHelpers";
import { logReportView } from "../../api/reportLogApi";
import { formatYmd } from "../../utils/dateRange";

const PALETTE = ["#41B883", "#E46651", "#00D8FF", "#DD1B16", "#23e033", "#32aebe"];

const SalesChannelsPage = observer(() => {
  const [selectedServer, setSelectedServer] = useState(periodPrefsStore.selectedServer);
  const [fromDate, setFromDate] = useState(periodPrefsStore.fromDate);
  const [toDate, setToDate] = useState(periodPrefsStore.toDate);
  const [quickRange, setQuickRange] = useState(periodPrefsStore.quickRange);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [originsResult, setOriginsResult] = useState(null);
  const [originsLoading, setOriginsLoading] = useState(false);
  const [originsError, setOriginsError] = useState(null);

  const load = useCallback(
    async (from = fromDate, to = toDate) => {
      if (!selectedServer || !from || !to) return;
      setLoading(true);
      setError(null);
      try {
        setResult(await getOrderTypes(selectedServer, from, to));
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }

      // Отдельный, последовательный (не Promise.all) запрос после первого —
      // iiko не любит параллельные запросы в рамках одной сессии логина.
      setOriginsLoading(true);
      setOriginsError(null);
      try {
        setOriginsResult(await getOrderOrigins(selectedServer, from, to));
      } catch (e) {
        setOriginsError(e);
      } finally {
        setOriginsLoading(false);
      }
    },
    [selectedServer, fromDate, toDate],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      if (!fromDate || !toDate) {
        const today = new Date();
        const past = new Date(today);
        past.setDate(today.getDate() - 7);
        setFromDate(formatYmd(past));
        setToDate(formatYmd(today));
      }
      logReportView({ event: "report_view", reportKey: "sales_channels", reportTitle: "Каналы продаж", userId, path: "/sales-channels" });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (selectedServer) periodPrefsStore.setServer(selectedServer);
  }, [selectedServer]);

  useEffect(() => {
    if (selectedServer && fromDate && toDate) load(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServer, fromDate, toDate]);

  const handleRefresh = (from, to) => {
    periodPrefsStore.setPeriod(from, to, quickRange);
    load(from, to);
  };

  const channels = result?.channels || [];
  const pie = buildPieItems(channels.map((c) => c.name), channels.map((c) => c.revenue), PALETTE);

  const origins = originsResult?.origins || [];
  const pieOrigins = buildPieItems(origins.map((o) => o.name), origins.map((o) => o.revenue), PALETTE);

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Каналы продаж</h4>

      <h5 className="mb-2">Тип заказа</h5>
      <p className="text-body-secondary small">
        Разбивка выручки по типу заказа (зал / доставка курьером / доставка самовывоз и т.п.) — группировка OLAP по полю
        OrderType.
      </p>
      <ServerSelect servers={settingsStore.servers} value={selectedServer} onChange={(e) => setSelectedServer(e.target.value)} />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={handleRefresh}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={loading}
        loading={loading}
      />
      <ErrorAlert error={error} />
      <CCard className="mt-3">
        <CCardBody>
          {loading && !result && <SkeletonChart />}
          {result?.note && <div className="small text-body-secondary mb-2">{result.note}</div>}
          {!loading && channels.length > 0 && (
            <div className="d-flex flex-column flex-md-row gap-3 align-items-start">
              <div style={{ width: 200, flex: "0 0 auto" }}>
                <CChartDoughnut
                  data={{
                    labels: pie.map((x) => x.label),
                    datasets: [{ backgroundColor: pie.map((x) => x.color), data: pie.map((x) => x.value) }],
                  }}
                  options={{ plugins: { legend: { display: false } } }}
                />
              </div>
              <CTable small hover responsive className="mb-0 flex-grow-1">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell />
                    <CTableHeaderCell>Тип заказа</CTableHeaderCell>
                    <CTableHeaderCell>Выручка</CTableHeaderCell>
                    <CTableHeaderCell>Гости</CTableHeaderCell>
                    <CTableHeaderCell>Чеки</CTableHeaderCell>
                    <CTableHeaderCell>Доля</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {channels.map((c, i) => (
                    <CTableRow key={c.name}>
                      <CTableDataCell>
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            background: pie[i]?.color,
                          }}
                        />
                      </CTableDataCell>
                      <CTableDataCell>{c.name}</CTableDataCell>
                      <CTableDataCell>{fmtInt(c.revenue)} ₽</CTableDataCell>
                      <CTableDataCell>{fmtInt(c.guests)}</CTableDataCell>
                      <CTableDataCell>{fmtInt(c.orders)}</CTableDataCell>
                      <CTableDataCell>{c.share_percent}%</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}
          {!loading && channels.length === 0 && !error && (
            <div className="text-body-secondary small">Нет данных за период.</div>
          )}
        </CCardBody>
      </CCard>

      <h5 className="mt-4 mb-2">Источник заказа</h5>
      <p className="text-body-secondary small">
        Отдельное измерение от типа заказа выше — не зал/доставка/самовывоз, а через что конкретно пришёл заказ:
        сайт/приложение (iikoWeb), мобильное приложение, киоск, колл-центр, конкретный агрегатор по его внутреннему
        ключу. Группировка OLAP по полю OriginName — именно это поле однозначно соответствует платформе, которая
        берёт комиссию (один и тот же тип заказа может объединять несколько разных агрегаторов).
      </p>
      <CCard>
        <CCardBody>
          {originsLoading && !originsResult && <SkeletonChart />}
          <ErrorAlert error={originsError} />
          {originsResult?.note && <div className="small text-body-secondary mb-2">{originsResult.note}</div>}
          {!originsLoading && origins.length > 0 && (
            <div className="d-flex flex-column flex-md-row gap-3 align-items-start">
              <div style={{ width: 200, flex: "0 0 auto" }}>
                <CChartDoughnut
                  data={{
                    labels: pieOrigins.map((x) => x.label),
                    datasets: [{ backgroundColor: pieOrigins.map((x) => x.color), data: pieOrigins.map((x) => x.value) }],
                  }}
                  options={{ plugins: { legend: { display: false } } }}
                />
              </div>
              <CTable small hover responsive className="mb-0 flex-grow-1">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell />
                    <CTableHeaderCell>Источник</CTableHeaderCell>
                    <CTableHeaderCell>Выручка</CTableHeaderCell>
                    {originsResult?.commissions_configured && (
                      <>
                        <CTableHeaderCell>Комиссия</CTableHeaderCell>
                        <CTableHeaderCell>Чистыми</CTableHeaderCell>
                      </>
                    )}
                    <CTableHeaderCell>Гости</CTableHeaderCell>
                    <CTableHeaderCell>Чеки</CTableHeaderCell>
                    <CTableHeaderCell>Доля</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {origins.map((o, i) => (
                    <CTableRow key={o.name}>
                      <CTableDataCell>
                        <span
                          style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: 2,
                            background: pieOrigins[i]?.color,
                          }}
                        />
                      </CTableDataCell>
                      <CTableDataCell>{o.name}</CTableDataCell>
                      <CTableDataCell>{fmtInt(o.revenue)} ₽</CTableDataCell>
                      {originsResult?.commissions_configured && (
                        <>
                          <CTableDataCell className="text-body-secondary">
                            {o.commission_percent > 0 ? `${fmtInt(o.commission_amount)} ₽ (${o.commission_percent}%)` : "—"}
                          </CTableDataCell>
                          <CTableDataCell className="fw-semibold">{fmtInt(o.net_revenue)} ₽</CTableDataCell>
                        </>
                      )}
                      <CTableDataCell>{fmtInt(o.guests)}</CTableDataCell>
                      <CTableDataCell>{fmtInt(o.orders)}</CTableDataCell>
                      <CTableDataCell>{o.share_percent}%</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
                {originsResult?.commissions_configured && (
                  <tfoot>
                    <CTableRow>
                      <CTableDataCell />
                      <CTableDataCell className="fw-semibold">Итого</CTableDataCell>
                      <CTableDataCell className="fw-semibold">{fmtInt(originsResult.total_revenue)} ₽</CTableDataCell>
                      <CTableDataCell />
                      <CTableDataCell className="fw-semibold">{fmtInt(originsResult.total_net_revenue)} ₽</CTableDataCell>
                      <CTableDataCell colSpan={3} />
                    </CTableRow>
                  </tfoot>
                )}
              </CTable>
            </div>
          )}
          {!originsLoading && origins.length > 0 && !originsResult?.commissions_configured && (
            <div className="small text-body-secondary mt-2">
              Чтобы видеть чистую выручку за вычетом комиссии агрегаторов — задайте % комиссии в Настройках сервера →
              «Комиссии».
            </div>
          )}
          {!originsLoading && origins.length === 0 && !originsError && (
            <div className="text-body-secondary small">Нет данных за период.</div>
          )}
        </CCardBody>
      </CCard>
    </CContainer>
  );
});

export default SalesChannelsPage;
